import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipForward, Volume2, Captions } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fileUrl } from "../lib/api";

export function NarrationPlayer({ chapter, onPanel }) {
  const [voices, setVoices] = useState([]);
  const [narrator, setNarrator] = useState("fable");
  const [map, setMap] = useState(chapter.voice_map || {});
  const [speed, setSpeed] = useState(1);
  const [vol, setVol] = useState(1);
  const [state, setState] = useState("idle");
  const [pos, setPos] = useState({ p: 0, l: -1 });
  const [caption, setCaption] = useState("");
  const audio = useRef(new Audio());
  const stop = useRef(false);
  const chars = [...new Set(chapter.panels.flatMap((p) => p.dialogue.map((d) => d.character)))];
  useEffect(() => { api.get("/voices").then((r) => setVoices(r.data)); const a = audio.current; return () => a.pause(); }, []);
  useEffect(() => { audio.current.playbackRate = speed; audio.current.volume = vol; }, [speed, vol]);

  const lines = (pi) => [{ l: -1, text: chapter.panels[pi].narration, who: "Narrator" }, ...chapter.panels[pi].dialogue.map((d, l) => ({ l, text: d.text, who: d.character }))].filter((x) => x.text?.trim());
  const playFrom = async (pi, li) => {
    stop.current = false; setState("playing");
    for (let p = pi; p < chapter.panels.length; p++) {
      onPanel?.(p);
      for (const ln of lines(p).filter((x) => p > pi || x.l >= li)) {
        if (stop.current) return;
        setPos({ p, l: ln.l }); setCaption(`${ln.who}: ${ln.text}`);
        try {
          const { data } = await api.post("/tts", { chapter_id: chapter.id, panel_index: p, line_index: ln.l, voice: ln.l < 0 ? narrator : map[ln.who] || "alloy" });
          if (!data.audio_url) continue;
          await new Promise((res) => { const a = audio.current; a.src = fileUrl(data.audio_url); a.playbackRate = speed; a.volume = vol; a.onended = res; a.onerror = res; a.play().catch(res); });
        } catch (e) { toast.error(errMsg(e)); setState("idle"); return; }
      }
    }
    setState("idle"); setCaption("");
  };
  const pause = () => { stop.current = true; audio.current.pause(); setState("paused"); };
  const resume = () => playFrom(pos.p, pos.l);
  const next = () => { pause(); const n = Math.min(pos.p + 1, chapter.panels.length - 1); setPos({ p: n, l: -1 }); setTimeout(() => playFrom(n, -1), 50); };

  return (
    <div data-testid="narration-player" className="sticky bottom-16 md:bottom-4 z-30 jx-glass border border-[var(--jx-border)] rounded-2xl p-3 mt-4">
      {caption && <div data-testid="narration-caption" aria-live="polite" className="text-sm mb-2 px-1 flex gap-2"><Captions size={16} className="jx-mint shrink-0 mt-0.5" />{caption}</div>}
      <div className="flex flex-wrap items-center gap-2">
        {state === "playing"
          ? <button data-testid="narration-pause-btn" onClick={pause} className="jx-btn w-11 h-11 grid place-items-center"><Pause size={18} /></button>
          : <button data-testid="narration-play-btn" onClick={state === "paused" ? resume : () => playFrom(0, -1)} className="jx-btn w-11 h-11 grid place-items-center"><Play size={18} /></button>}
        <button data-testid="narration-next-btn" onClick={next} className="w-11 h-11 grid place-items-center rounded-full jx-surface"><SkipForward size={16} /></button>
        <select data-testid="narrator-voice-select" value={narrator} onChange={(e) => setNarrator(e.target.value)} className="bg-transparent border rounded-full px-3 h-11 text-sm">
          {voices.map((v) => <option key={v.id} value={v.id}>Narrator · {v.label} ({v.gender}, {v.tone})</option>)}
        </select>
        <select data-testid="narration-speed-select" value={speed} onChange={(e) => setSpeed(+e.target.value)} className="bg-transparent border rounded-full px-3 h-11 text-sm">
          {[0.75, 1, 1.25, 1.5, 2].map((s) => <option key={s} value={s}>{s}x</option>)}
        </select>
        <label className="flex items-center gap-1 text-xs jx-muted"><Volume2 size={14} /><input data-testid="narration-volume" type="range" min="0" max="1" step="0.1" value={vol} onChange={(e) => setVol(+e.target.value)} className="w-20 accent-[var(--jx-pink)]" /></label>
      </div>
      {chars.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{chars.map((c) => (
        <select key={c} data-testid={`character-voice-${c}`} value={map[c] || "alloy"} onChange={(e) => setMap({ ...map, [c]: e.target.value })} className="bg-transparent border rounded-full px-2 h-9 text-xs">
          {voices.map((v) => <option key={v.id} value={v.id}>{c} · {v.label}</option>)}
        </select>))}</div>}
    </div>
  );
}
